import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import {
  Container,
  TextField,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ReactQuill from "react-quill";
import DatePicker from "react-datepicker";
import "react-quill/dist/quill.snow.css";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const EditModal = ({
  modelTitle,
  content,
  okText,
  cancelText,
  title,
  formFields,
  onSubmit,
  buttonName,
  editButton,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Open modal and initialize form data
  const showModal = () => {
    setIsModalOpen(true);
    const initialFormData = formFields.reduce((acc, field) => {
      if (field.type === "date") {
        // Initialize date fields
        if (field.name === "startDate") {
          setStartDate(field.value ? new Date(field.value) : null);
        } else if (field.name === "endDate") {
          setEndDate(field.value ? new Date(field.value) : null);
        }
        return acc;
      }
      return { ...acc, [field.name]: field.value };
    }, {});
    setFormData(initialFormData);
  };

  // Update formData when startDate or endDate changes
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      startDate: startDate ? moment(startDate).format("MM/DD/YYYY") : null,
      endDate: endDate ? moment(endDate).format("MM/DD/YYYY") : null,
    }));
  }, [startDate, endDate]);

  // Close modal
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle input change for text fields
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Quill editor change for description field
  const handleQuillChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  // Handle Date
  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange = (date) => setEndDate(date);

  // Handle form submission
  const { confirm } = Modal;
  const handleSubmit = (e) => {
    e.preventDefault();

    confirm({
      title: modelTitle,
      content: content,
      okText: okText,
      cancelText: cancelText,
      onOk: async () => {
        onSubmit(formData);
        setIsModalOpen(false);
      },
    });
  };

  return (
    <>
      {editButton === "Edit" ? (
        <Button
          onClick={showModal}
          variant="outlined"
          className="courses_desc"
          sx={{
            color: "primary",
            mt: 1,
            borderRadius: "50px",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            letterSpacing: ".1rem",
          }}
        >
          Edit
        </Button>
      ) : (
        <IconButton aria-label="edit" onClick={showModal}>
          <EditIcon sx={{ color: "#1976d2" }} />
        </IconButton>
      )}

      <Modal
        title={title}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={false}
        fullWidth
      >
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="form_style border-style"
        >
          {formFields.map((field, index) => (
            <div key={index}>
              {field.type === "description" ? (
                <>
                  <h4
                    style={{
                      marginTop: "-12px",
                    }}
                  >
                    {field.label}
                  </h4>
                  <ReactQuill
                    value={formData[field.name] || ""}
                    onChange={(value) => handleQuillChange(field.name, value)}
                    placeholder={field.placeholder}
                    style={{
                      marginBottom: "22px",
                    }}
                  />
                </>
              ) : field.type === "dropDown" ? (
                <Box sx={{ mb: 3 }} key={field.name}>
                  <FormControl fullWidth>
                    <InputLabel id="batch-class-mode-label">
                      {field.label}
                    </InputLabel>
                    <Select
                      labelId="batch-class-mode-label"
                      id="batch-class-mode"
                      label={field.label}
                      name={field.name}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                    >
                      <MenuItem disabled>Select Batch & Class Mode</MenuItem>
                      <MenuItem value="Offline">Offline</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : field.type === "date" ? (
                <Box className="Date" key={field.name}>
                  <Box sx={{ mb: 3 }}>
                    <h4>{field.label}</h4>
                    <DatePicker
                      showIcon
                      toggleCalendarOnIconClick
                      isClearable
                      placeholderText={field.placeholder}
                      selected={
                        field.name === "startDate" ? startDate : endDate
                      }
                      onChange={
                        field.name === "startDate"
                          ? handleStartDateChange
                          : handleEndDateChange
                      }
                      minDate={
                        field.name === "endDate" ? startDate : new Date()
                      }
                      maxDate={field.name === "startDate" ? endDate : null}
                      required
                    />

                  </Box>
                </Box>
              ) : (
                <TextField
                  sx={{
                    mb: 3,
                    "& .MuiInputBase-root": {
                      color: "text.secondary",
                    },
                    fieldset: { borderColor: "rgb(231, 235, 240)" },
                  }}
                  fullWidth
                  label={field.label}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ""}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </div>
          ))}

          <Button variant="contained" type="submit">
            {buttonName}
          </Button>
        </Container>
      </Modal>
    </>
  );
};

export default EditModal;
